import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import SendIcon from "@material-ui/icons/Send";
import CircularProgress from "@material-ui/core/CircularProgress";
import DraftsIcon from "@material-ui/icons/Drafts";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import MessageIcon from "@material-ui/icons/Message";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import RefreshIcon from "@material-ui/icons/Refresh";
import TimelineMessage from "../../widgets/TimelineMessage";
import useUserState from "../../hooks/useUserState";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import "../../../styles/consultText.css";

const REFRESH_COOLDOWN_SECONDS = 10;

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
    border: "2px solid #dfdfdf",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: "#f8f9fa",
    minHeight: 64,
  },
  avatar: {
    width: 44,
    height: 44,
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
    fontWeight: 600,
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontWeight: 600,
    fontSize: "1rem",
    lineHeight: 1.3,
  },
  dateText: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  infoSection: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    flexWrap: "wrap",
  },
  chip: {
    fontSize: "0.75rem",
    height: 26,
    fontWeight: 500,
  },
  messageCard: {
    margin: theme.spacing(2, 2.5),
    padding: theme.spacing(2),
    backgroundColor: "#f8f9fa",
    borderRadius: theme.spacing(1),
    border: "1px solid #e9ecef",
  },
  messageLabel: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  messageContent: {
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  threadSection: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(0, 1),
  },
  threadHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 2.5),
  },
  threadLabel: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  replySection: {
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    padding: theme.spacing(2, 2.5),
    backgroundColor: "#f8f9fa",
  },
  sendButton: {
    marginTop: theme.spacing(1.5),
    borderRadius: 20,
    textTransform: "none",
    fontWeight: 500,
    padding: theme.spacing(0.75, 3),
  },
  refreshIcon: {
    transition: "transform 0.3s ease",
  },
  refreshIconSpinning: {
    animation: "$spin 1s linear infinite",
  },
  cooldownText: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

export default function MessageBody(props) {
  const localState = useUserState();
  const { t } = useBDocTranslator();
  const classes = useStyles();

  // Refresh rate limiting state
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownTimerRef = useRef(null);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldownRemaining(REFRESH_COOLDOWN_SECONDS);
    cooldownTimerRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleRefresh = useCallback(async () => {
    if (refreshing || cooldownRemaining > 0 || !props.onRefreshThread) return;

    setRefreshing(true);
    try {
      await props.onRefreshThread();
    } catch (error) {
      console.error("Failed to refresh thread:", error);
    } finally {
      setRefreshing(false);
      startCooldown();
    }
  }, [refreshing, cooldownRemaining, props.onRefreshThread, startCooldown]);

  const isRefreshDisabled = refreshing || cooldownRemaining > 0 || props.loading;

  const getRefreshTooltip = () => {
    if (refreshing) return t("Refreshing...");
    if (cooldownRemaining > 0) {
      return `${t("Wait")} ${cooldownRemaining}s`;
    }
    return t("Refresh conversation");
  };

  const otherUser = props.messageSelected.other_user?.extendedData;
  const fullName = otherUser
    ? `${otherUser.firstName || ""} ${otherUser.lastname || ""}`.trim()
    : t("Unknown");
  const initial = (otherUser?.firstName || "?")[0].toUpperCase();

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <IconButton onClick={props.handleBack} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Avatar className={classes.avatar}>{initial}</Avatar>
        <div className={classes.headerInfo}>
          <Typography className={classes.userName}>{fullName}</Typography>
          <Typography className={classes.dateText}>
            {new Date(props.messageSelected.createdAt).toLocaleString()}
          </Typography>
        </div>

        {/* Refresh Button */}
        {props.onRefreshThread && (
          <Tooltip title={getRefreshTooltip()}>
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshDisabled}
                aria-label={t("Refresh conversation")}
              >
                <RefreshIcon
                  className={
                    refreshing
                      ? classes.refreshIconSpinning
                      : classes.refreshIcon
                  }
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={t("Mark as Read")}>
          <span>
            <IconButton
              onClick={() =>
                props.handleMarkRead(props?.messageSelected?.send_to_userid)
              }
              disabled={props.loading}
            >
              <DraftsIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      {/* Info Chips */}
      <div className={classes.infoSection}>
        <Chip
          icon={<MessageIcon />}
          label={t("Text Consultation")}
          size="small"
          variant="outlined"
          color="primary"
          className={classes.chip}
        />
        <Chip
          icon={<AttachMoneyIcon />}
          label={t("Free")}
          size="small"
          variant="outlined"
          className={classes.chip}
        />
      </div>

      {/* Original Message */}
      <Box className={classes.messageCard}>
        <Typography className={classes.messageLabel}>
          {t("Original Message")}
        </Typography>
        <Typography className={classes.messageContent}>
          {props.message}
        </Typography>
      </Box>

      {/* Timeline / Conversation Thread */}
      {props.messageSelected.thread?.length > 0 && (
        <div className={classes.threadSection}>
          <div className={classes.threadHeader}>
            <Typography className={classes.threadLabel}>
              {t("Conversation")}
            </Typography>
            {cooldownRemaining > 0 && (
              <Typography className={classes.cooldownText}>
                {t("Refresh available in")} {cooldownRemaining}s
              </Typography>
            )}
          </div>
          <TimelineMessage messages={props.messageSelected} />
        </div>
      )}

      {/* Reply Section */}
      <div className={classes.replySection}>
        <TextField
          id="Response"
          placeholder={t("Write your answer here")}
          multiline
          fullWidth
          rows={4}
          value={props.messageResponse}
          variant="outlined"
          onChange={(e) => props.handleSetResponse(e.target.value)}
        />
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            className={classes.sendButton}
            endIcon={
              props.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            disabled={props.loading}
            onClick={() => {
              if (
                localState[0].user.id ===
                props?.messageSelected?.send_to_userid
              ) {
                props.handleResponse(props?.messageSelected?.send_by_userid);
              } else {
                props.handleResponse(props?.messageSelected?.send_to_userid);
              }
            }}
          >
            {t("Send")}
          </Button>
        </Box>
      </div>
    </div>
  );
}
